import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { openWhatsApp } from '../../utils/whatsapp';

export default function HeroSection() {
  const siteConfig = useSiteConfig();
  const { hero, contact } = siteConfig;
  const [ctaLabel, setCtaLabel] = useState(hero.ctaText);

  const handleCta = () => {
    setCtaLabel('⏳ 正在開啟 WhatsApp...');
    openWhatsApp(contact.whatsapp, hero.whatsappMessage);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-circuit-grid">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={hero.backgroundImage}
          alt="Background"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-deep-navy/70 via-deep-navy/80 to-deep-navy" />
        <div className="absolute inset-0 bg-gradient-to-tr from-neon-purple/10 via-transparent to-neon-cyan/10" />
      </div>

      {/* Floating decorative glows */}
      <div className="absolute top-24 left-8 w-64 h-64 bg-neon-purple/20 rounded-full blur-3xl animate-float-slow" />
      <div className="absolute bottom-24 right-8 w-72 h-72 bg-neon-cyan/15 rounded-full blur-3xl animate-float-slow" />

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto pt-16">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neon-cyan/30 bg-neon-cyan/5 text-neon-cyan text-xs md:text-sm font-medium mb-6"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse" />
          {hero.badge}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-5xl md:text-7xl lg:text-8xl font-black leading-tight mb-4"
        >
          <span className="text-white">{hero.headline}</span>
          <span className="text-gradient-neon">{hero.highlight}</span>
        </motion.h1>

        {hero.video && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6"
          >
            <video
              src={hero.video}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="mx-auto max-w-full h-auto rounded-xl border border-neon-cyan/20 shadow-lg"
            />
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="inline-block px-6 py-2 rounded-full border border-neon-purple/40 bg-neon-purple/10 text-white text-base md:text-lg font-medium mb-8"
        >
          {hero.subheadline}
        </motion.div>

        {/* Feature pills: 自動拍+剪+寫+發+管 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-10"
        >
          {hero.features.map((f, i) => (
            <span key={i} className="flex items-center gap-2">
              <span className="px-4 py-2 rounded-xl neon-card text-sm md:text-base font-semibold text-neon-cyan-glow">
                {f}
              </span>
              {i < hero.features.length - 1 && (
                <span className="text-neon-gold text-lg font-bold">+</span>
              )}
            </span>
          ))}
        </motion.div>

        <motion.button
          onClick={handleCta}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="inline-block px-8 py-4 bg-gradient-to-r from-neon-cyan via-neon-blue to-neon-purple text-deep-navy font-bold text-base md:text-lg rounded-full transition-all duration-300 hover:brightness-110 hover:-translate-y-0.5 animate-pulse-glow"
        >
          {ctaLabel}
        </motion.button>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-4 text-sm text-gray-400"
        >
          {hero.ctaSubtext}
        </motion.p>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-neon-cyan/60"
      >
        <ChevronDown size={32} />
      </motion.div>
    </section>
  );
}
